import { BaseApiService } from "@mirage/services/BaseApiService";
import { UserStore } from "@mirage/store/UserStore";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import {
    ChangePasswordRequest,
    ChangePasswordResult,
    CheckEmailExistsRequest,
    CheckEmailExistsResult,
    CheckPasswordLengthRequest,
    CheckPasswordLengthResult,
    ForgotPasswordRequest,
    ForgotPasswordResult,
    LoginRequest,
    LoginResult,
    SetNewPasswordRequest,
    SetNewPasswordResult,
    SignUpRequest,
    SignUpResult,
    UpdateUserRequest,
    UpdateUserResult,
    ValidateForgottenPasswordTokenRequest,
    ValidateForgottenPasswordTokenResult,
    VerifyEmailResult,
    VerifyRequest,
} from "@shared/mirage/core/contracts/User";
import dayjs from "dayjs";
import { IUserService } from "@shared/mirage/services/IUserService";
export class UserService extends BaseApiService implements IUserService {
    static _lastCheckTime: Date | null = null;
    static _lastResult = false;
    static SetLastAuthCheckResult(result: boolean): void {
        UserService._lastCheckTime = new Date();
        UserService._lastResult = result;
    }

    constructor() {
        super("publicportal/users");
    }
    async logout() {
        await this.httpInstance().post("/logout");
        localStorage.removeItem("users.user");
        UserService.SetLastAuthCheckResult(false);
    }
    async checkAuth(): Promise<boolean> {
        if (UserService._lastCheckTime === null) {
            const result = (await this.httpInstance().get("/checkAuth")).status === 200;
            UserService.SetLastAuthCheckResult(result);
        } else {
            const now = new Date();
            const diffInMinutes = dayjs(now).diff(UserService._lastCheckTime, "minutes");
            if (diffInMinutes > 3) {
                const result = (await this.httpInstance().get("/checkAuth")).status === 200;
                UserService.SetLastAuthCheckResult(result);
            }
        }
        return UserService._lastResult;
    }
    async register(signUpRequest: SignUpRequest): Promise<SignUpResult> {
        const response = await this.httpInstance().post("/register", signUpRequest);
        return response.data;
    }
    async login(loginRequest: LoginRequest): Promise<LoginResult> {
        const response = await this.httpInstance().post("/login", loginRequest);
        const result = response.data as LoginResult;
        if (result.userId) {
            localStorage.setItem(
                "users.user",
                JSON.stringify({
                    id: result.userId,
                    first_name: result.userName,
                    last_name: result.userLastName,
                    custom_properties: result.userCustomProperties,
                    expiryDate: result.expiryDate,
                } as UserStore)
            );
            UserService.SetLastAuthCheckResult(true);
        }
        return result;
    }
    async verifyEmail(verifyEmailRequest: VerifyRequest): Promise<VerifyEmailResult> {
        const response = await this.httpInstance().post("/verifyemail", verifyEmailRequest);
        return response.data;
    }
    async checkEmailExists(checkEmailExistsRequest: CheckEmailExistsRequest): Promise<CheckEmailExistsResult> {
        const response = await this.httpInstance().post("/checkEmailExists", checkEmailExistsRequest);
        return response.data;
    }
    async checkPasswordLength(checkPasswordLengthRequest: CheckPasswordLengthRequest): Promise<CheckPasswordLengthResult> {
        const response = await this.httpInstance().post("/checkPasswordLength", checkPasswordLengthRequest);
        return response.data;
    }
    async getUserById(): Promise<IUser> {
        const user = UserService.getLoggedInUser();
        const response = await this.httpInstance().get(`/${user.id}`);
        return response.data;
    }
    async updateUserById(updateUserRequest: Omit<UpdateUserRequest, "userId">): Promise<UpdateUserResult> {
        const user = UserService.getLoggedInUser();
        const response = await this.httpInstance().post(`/${user.id}`, updateUserRequest);
        return response.data;
    }
    async changePassword(changePasswordRequest: Omit<ChangePasswordRequest, "userId">): Promise<ChangePasswordResult> {
        const response = await this.httpInstance().post("/changePassword", changePasswordRequest);
        return response.data;
    }
    async forgotPassword(forgotPasswordRequest: ForgotPasswordRequest): Promise<ForgotPasswordResult> {
        const response = await this.httpInstance().post(`/forgotPassword`, forgotPasswordRequest);
        return response.data;
    }

    async validateForgottenPasswordToken(validateForgottenPasswordTokenRequest: ValidateForgottenPasswordTokenRequest): Promise<ValidateForgottenPasswordTokenResult> {
        const response = await this.httpInstance().post(`/validateForgottenPasswordToken`, validateForgottenPasswordTokenRequest);
        return response.data;
    }

    async setNewPassword(setNewPasswordRequest: Omit<SetNewPasswordRequest, "userId">): Promise<SetNewPasswordResult> {
        const response = await this.httpInstance().post(`/setnewpassword`, setNewPasswordRequest);
        return response.data;
    }

    async replaceUserInLocalStorage() {
        const result = await this.getUserById();
        localStorage.removeItem("users.user");
        localStorage.setItem("users.user", JSON.stringify({ id: result.id, first_name: result.first_name, last_name: result.last_name } as IUser));
    }

    static getLoggedInUser(): Omit<UserStore, "permissions"> {
        const userString = localStorage.getItem("users.user");
        const user: UserStore = userString && JSON.parse(userString);
        return user;
    }
}
